import { useState, useRef } from 'react';
import { ContextMenuItem } from '@/components/ui/ContextMenu';
import { ItemType } from '../types';

interface UseContextMenuProps {
  selectedEntities: Set<string>;
  setSelectedEntities: React.Dispatch<React.SetStateAction<Set<string>>>;
  setLastSelectedEntity: React.Dispatch<React.SetStateAction<any>>;
  allowCreation?: boolean;
  itemType?: ItemType;
  onFolderCreate?: (parentId?: string) => void;
  oncreateObject?: (folderId?: string) => void;
  onCreateBlueprint?: (folderId?: string) => void;
  onCreateDocument?: (folderId?: string) => void;
  onCreateDialog?: (folderId?: string) => void;
  onCreateFlow?: (folderId?: string) => void;
  onCreateVariable?: (folderId?: string) => void;
  handleBatchDelete: () => void;
}

interface UseContextMenuReturn {
  contextMenu: {
    isOpen: boolean;
    position: { x: number; y: number };
  };
  addButtonRef: React.RefObject<HTMLButtonElement>;
  handleAddButtonClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  closeContextMenu: () => void;
  getContextMenuItems: () => ContextMenuItem[];
}

/**
 * Hook to manage context menu state and items
 */
export const useContextMenu = ({
  selectedEntities,
  setSelectedEntities,
  setLastSelectedEntity,
  allowCreation = true,
  itemType = 'glossary',
  onFolderCreate,
  oncreateObject,
  onCreateBlueprint,
  onCreateDocument,
  onCreateDialog,
  onCreateFlow,
  onCreateVariable,
  handleBatchDelete,
}: UseContextMenuProps): UseContextMenuReturn => {
  const [contextMenu, setContextMenu] = useState<{
    isOpen: boolean;
    position: { x: number; y: number };
  }>({
    isOpen: false,
    position: { x: 0, y: 0 },
  });
  const addButtonRef = useRef<HTMLButtonElement>(null);

  const handleAddButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Get button position for context menu
    const rect = addButtonRef.current?.getBoundingClientRect();
    if (rect) {
      setContextMenu({
        isOpen: true,
        position: { x: rect.left, y: rect.bottom + 4 },
      });
    }
  };

  const closeContextMenu = () => {
    setContextMenu({ isOpen: false, position: { x: 0, y: 0 } });
  };

  const getContextMenuItems = (): ContextMenuItem[] => {
    const items: ContextMenuItem[] = [];

    // If multiple items are selected, show batch operations
    if (selectedEntities.size > 1) {
      items.push({
        id: 'delete-selected',
        label: `Delete ${selectedEntities.size} items`,
        icon: 'fa-trash',
        destructive: true,
        onClick: () => {
          handleBatchDelete();
          closeContextMenu();
        },
      });
      
      items.push({
        id: 'deselect-all',
        label: 'Deselect All',
        icon: 'fa-times',
        onClick: () => {
          setSelectedEntities(new Set());
          setLastSelectedEntity(null);
          closeContextMenu();
        },
      });
      
      return items;
    }

    // Add folder creation option
    if (allowCreation && onFolderCreate) {
      items.push({
        id: 'create-folder',
        label: 'Create Folder',
        icon: 'fa-plus',
        onClick: () => {
          onFolderCreate(); // Create in root
          closeContextMenu();
        },
      });
    }

    // Add item creation options based on itemType
    if (allowCreation) {
      if (itemType === 'glossary' && oncreateObject) {
        items.push({
          id: 'create-glossary-item',
          label: 'Create Object',
          icon: 'fa-book',
          onClick: () => {
            oncreateObject(); // Create in root (no folder)
            closeContextMenu();
          },
        });
      }
      
      if (itemType === 'blueprint' && onCreateBlueprint) {
        items.push({
          id: 'create-blueprint',
          label: 'Create Template',
          icon: 'fa-file-lines',
          onClick: () => {
            onCreateBlueprint(); // Create in root (no folder)
            closeContextMenu();
          },
        });
      }
      
      if (itemType === 'document' && onCreateDocument) {
        items.push({
          id: 'create-document',
          label: 'Create Document',
          icon: 'fa-file-text',
          onClick: () => {
            onCreateDocument(); // Create in root (no folder)
            closeContextMenu();
          },
        });
      }
      
      if (itemType === 'dialog' && onCreateDialog) {
        items.push({
          id: 'create-dialog',
          label: 'Create Dialog',
          icon: 'fa-comments',
          onClick: () => {
            onCreateDialog(); // Create in root (no folder)
            closeContextMenu();
          },
        });
      }

      if (itemType === 'flow' && onCreateFlow) {
        items.push({
          id: 'create-flow',
          label: 'Create Logic Flow',
          icon: 'fa-diagram-project',
          onClick: () => {
            onCreateFlow(); // Create in root (no folder)
            closeContextMenu();
          },
        });
      }

      if (itemType === 'variable' && onCreateVariable) {
        items.push({
          id: 'create-variable',
          label: 'Create Variable',
          icon: 'fa-code',
          onClick: () => {
            onCreateVariable(); // Create in root (no folder)
            closeContextMenu();
          },
        });
      }
    }

    return items;
  };

  return {
    contextMenu,
    addButtonRef,
    handleAddButtonClick,
    closeContextMenu,
    getContextMenuItems,
  };
};
